import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ErrorNotice, Loading } from '../components/Feedback';
import { get, put } from '../lib/api';
import type { Brain } from '../types';

const arrayFields = [
  'variables',
  'researchQuestions',
  'objectives',
  'hypotheses',
  'indicators'
] as const;
const emptyBrain: Brain = {
  studentName: '',
  studentId: '',
  campus: '',
  faculty: '',
  studyProgram: '',
  degreeLevel: '',
  academicYear: '',
  researchTitle: '',
  researchObject: '',
  location: '',
  method: '',
  variables: [],
  population: '',
  sample: '',
  researchPeriod: '',
  researchQuestions: [],
  objectives: [],
  hypotheses: [],
  indicators: [],
  citationStyle: 'APA 7th',
  activeGuidelineRuleSetId: null,
  revision: 1
};

type FactStatus = 'draft' | 'confirmed' | 'protected';
type Foundation = {
  facts: Array<{ id: string; key: string; value: unknown; status: FactStatus; revision: number }>;
  terminology: Array<{
    preferredTerm: string;
    alternatives: string[];
    definition: string;
    status: FactStatus;
  }>;
  writingProfile: {
    name: string;
    language: string;
    formality: string;
    sentenceRhythm: string;
    transitionStyle: string;
    depthLevel: string;
    preferredTerms: string[];
    avoidedTerms: string[];
    sampleText: string;
    status: 'draft' | 'confirmed' | 'active';
  } | null;
};

export function BrainPage({ projectId }: { projectId: string }) {
  const [brain, setBrain] = useState<Brain>(emptyBrain);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [foundation, setFoundation] = useState<Foundation | null>(null);
  const [foundationSaved, setFoundationSaved] = useState('');

  const loadFoundation = useCallback(async (preserveDraft = false) => {
    try {
      const result = await get<Foundation>(`/api/projects/${projectId}/foundations`);
      setFoundation((current) => {
        if (!preserveDraft || !current) return result;

        const currentFactStatus = new Map(
          current.facts.map((fact) => [fact.id, fact.status])
        );

        return {
          ...result,
          facts: result.facts.map((fact) => ({
            ...fact,
            status: currentFactStatus.get(fact.id) ?? fact.status
          })),
          terminology: current.terminology,
          writingProfile: current.writingProfile
        };
      });
    } catch {
      if (!preserveDraft) setFoundation(null);
    }
  }, [projectId]);

  useEffect(() => {
    get<{ brain: Brain }>(`/api/projects/${projectId}/brain`)
      .then((data) => setBrain(data.brain))
      .catch((caught) =>
        setError(caught instanceof Error ? caught.message : 'Project Brain tidak dapat dimuat.')
      )
      .finally(() => setLoading(false));
    void loadFoundation();
  }, [loadFoundation, projectId]);

  function update<K extends keyof Brain>(key: K, value: Brain[K]) {
    setBrain((current) => ({ ...current, [key]: value }));
    setSaved('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const result = await put<{ revision: number; savedAt: string }>(
        `/api/projects/${projectId}/brain`,
        {
          ...brain,
          expectedRevision: brain.revision
        }
      );
      setBrain((current) => ({ ...current, revision: result.revision }));
      setSaved(
        `Tersimpan ${new Date(result.savedAt).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}`
      );
      // Project Brain dan Kunci Konsistensi memiliki lifecycle penyimpanan terpisah.
      // Jangan reload foundation di sini karena dapat menimpa draft Kunci yang belum disimpan.
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Project Brain gagal disimpan.');
    } finally {
      setSaving(false);
    }
  }

  async function saveFoundation() {
    if (!foundation?.writingProfile) return;
    setError('');
    try {
      await put(`/api/projects/${projectId}/foundations`, {
        facts: foundation.facts.map(({ id: factId, status }) => ({ id: factId, status })),
        terminology: foundation.terminology,
        writingProfile: foundation.writingProfile
      });
      setFoundationSaved('Kunci konsistensi tersimpan');
      await loadFoundation();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Fondasi konsistensi gagal disimpan.');
    }
  }

  if (loading) return <Loading label="Membuka Project Brain…" />;
  return (
    <div className="content-page brain-page">
      <div className="content-heading">
        <div>
          <span className="eyebrow">Single source of truth</span>
          <h1>Project Brain</h1>
          <p>Data di sini menjadi acuan dokumen, pedoman, validasi, dan setiap usulan Seno AI.</p>
        </div>
        <div className="save-indicator">{saved || `Revisi ${brain.revision}`}</div>
      </div>
      {error ? <ErrorNotice message={error} /> : null}
      <form onSubmit={submit} className="brain-form">
        <fieldset>
          <legend>Identitas akademik</legend>
          <div className="form-grid">
            <label>
              Nama mahasiswa
              <input
                value={brain.studentName}
                onChange={(e) => update('studentName', e.target.value)}
              />
            </label>
            <label>
              NIM
              <input
                value={brain.studentId}
                onChange={(e) => update('studentId', e.target.value)}
              />
            </label>
            <label className="span-2">
              Kampus
              <input value={brain.campus} onChange={(e) => update('campus', e.target.value)} />
            </label>
            <label>
              Fakultas
              <input value={brain.faculty} onChange={(e) => update('faculty', e.target.value)} />
            </label>
            <label>
              Program studi
              <input
                value={brain.studyProgram}
                onChange={(e) => update('studyProgram', e.target.value)}
              />
            </label>
            <label>
              Jenjang
              <select
                value={brain.degreeLevel}
                onChange={(e) => update('degreeLevel', e.target.value)}
              >
                <option value="">Pilih jenjang</option>
                <option>S1</option>
                <option>S2</option>
                <option>S3</option>
                <option>D4</option>
              </select>
            </label>
            <label>
              Tahun akademik
              <input
                value={brain.academicYear}
                onChange={(e) => update('academicYear', e.target.value)}
                placeholder="2026/2027"
              />
            </label>
          </div>
        </fieldset>
        <fieldset>
          <legend>Fondasi penelitian</legend>
          <div className="form-grid">
            <label className="span-2">
              Judul penelitian
              <textarea
                rows={3}
                value={brain.researchTitle}
                onChange={(e) => update('researchTitle', e.target.value)}
              />
            </label>
            <label>
              Objek penelitian
              <input
                value={brain.researchObject}
                onChange={(e) => update('researchObject', e.target.value)}
              />
            </label>
            <label>
              Lokasi
              <input value={brain.location} onChange={(e) => update('location', e.target.value)} />
            </label>
            <label className="span-2">
              Metode
              <textarea
                rows={2}
                value={brain.method}
                onChange={(e) => update('method', e.target.value)}
              />
            </label>
            <label>
              Populasi
              <input
                value={brain.population}
                onChange={(e) => update('population', e.target.value)}
              />
            </label>
            <label>
              Sampel
              <input value={brain.sample} onChange={(e) => update('sample', e.target.value)} />
            </label>
            <label>
              Periode penelitian
              <input
                value={brain.researchPeriod}
                onChange={(e) => update('researchPeriod', e.target.value)}
              />
            </label>
            <label>
              Gaya sitasi
              <select
                value={brain.citationStyle}
                onChange={(e) => update('citationStyle', e.target.value)}
              >
                <option>APA 7th</option>
                <option>IEEE</option>
                <option>Chicago</option>
                <option>Vancouver</option>
              </select>
            </label>
          </div>
        </fieldset>
        <fieldset>
          <legend>Logika dan istilah penelitian</legend>
          <div className="stacked-fields">
            {arrayFields.map((key) => (
              <label key={key}>
                {
                  (
                    {
                      variables: 'Variabel',
                      researchQuestions: 'Rumusan masalah',
                      objectives: 'Tujuan',
                      hypotheses: 'Hipotesis',
                      indicators: 'Definisi atau indikator utama'
                    } as const
                  )[key]
                }
                <textarea
                  rows={key === 'variables' ? 2 : 4}
                  value={brain[key].join('\n')}
                  onChange={(e) =>
                    update(
                      key,
                      e.target.value
                        .split('\n')
                        .map((item) => item.trim())
                        .filter(Boolean)
                    )
                  }
                />
                <small>Satu item per baris. Nilai ini menjadi fondasi Protected Facts.</small>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="form-submit">
          <span>
            Field kritis disimpan sebagai fakta terstruktur dan dapat dikonfirmasi atau dikunci.
          </span>
          <button className="button button-primary" disabled={saving}>
            {saving ? 'Menyimpan…' : 'Simpan Project Brain'}
          </button>
        </div>
      </form>
      {foundation?.writingProfile ? (
        <section className="foundation-editor">
          <div className="section-title">
            <div>
              <span className="eyebrow">Kunci konsistensi</span>
              <h2>Fakta, istilah, dan gaya penulisan</h2>
              <p>Nilai protected tidak boleh diubah oleh penyusunan, parafrase, atau revisi.</p>
            </div>
            <button className="button button-primary" onClick={saveFoundation}>
              Simpan kunci
            </button>
          </div>
          {foundationSaved ? <p className="success-note">{foundationSaved}</p> : null}
          <div className="fact-lock-list">
            {foundation.facts.map((fact, index) => (
              <div className="fact-lock-row" key={fact.id}>
                <div>
                  <strong>{fact.key.replaceAll('_', ' ')}</strong>
                  <span>
                    {Array.isArray(fact.value) ? fact.value.join('; ') : String(fact.value ?? '')}
                  </span>
                </div>
                <select
                  aria-label={`Status ${fact.key}`}
                  value={fact.status}
                  onChange={(event) =>
                    setFoundation((current) =>
                      current
                        ? {
                            ...current,
                            facts: current.facts.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, status: event.target.value as FactStatus }
                                : item
                            )
                          }
                        : current
                    )
                  }
                >
                  <option value="draft">Draft</option>
                  <option value="confirmed">Dikonfirmasi</option>
                  <option value="protected">Dikunci</option>
                </select>
              </div>
            ))}
          </div>
          <div className="form-grid compact-foundation">
            <label>
              Bahasa utama
              <input
                value={foundation.writingProfile.language}
                onChange={(event) =>
                  setFoundation({
                    ...foundation,
                    writingProfile: { ...foundation.writingProfile!, language: event.target.value }
                  })
                }
              />
            </label>
            <label>
              Jenjang gaya akademik
              <select
                value={foundation.writingProfile.depthLevel}
                onChange={(event) =>
                  setFoundation({
                    ...foundation,
                    writingProfile: {
                      ...foundation.writingProfile!,
                      depthLevel: event.target.value
                    }
                  })
                }
              >
                <option value="undergraduate">Sarjana</option>
                <option value="master">Magister</option>
                <option value="doctoral">Doktoral</option>
              </select>
            </label>
            <label className="span-2">
              Istilah wajib — satu per baris
              <textarea
                rows={3}
                value={foundation.writingProfile.preferredTerms.join('\n')}
                onChange={(event) =>
                  setFoundation({
                    ...foundation,
                    writingProfile: {
                      ...foundation.writingProfile!,
                      preferredTerms: event.target.value
                        .split('\n')
                        .map((item) => item.trim())
                        .filter(Boolean)
                    }
                  })
                }
              />
            </label>
            <label className="span-2">
              Istilah yang dihindari — satu per baris
              <textarea
                rows={3}
                value={foundation.writingProfile.avoidedTerms.join('\n')}
                onChange={(event) =>
                  setFoundation({
                    ...foundation,
                    writingProfile: {
                      ...foundation.writingProfile!,
                      avoidedTerms: event.target.value
                        .split('\n')
                        .map((item) => item.trim())
                        .filter(Boolean)
                    }
                  })
                }
              />
            </label>
            <label className="span-2">
              Contoh gaya tulisan
              <textarea
                rows={5}
                value={foundation.writingProfile.sampleText}
                onChange={(event) =>
                  setFoundation({
                    ...foundation,
                    writingProfile: {
                      ...foundation.writingProfile!,
                      sampleText: event.target.value
                    }
                  })
                }
              />
            </label>
          </div>
        </section>
      ) : null}
    </div>
  );
}
